import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SystemCollectionCard } from './SystemCollectionCard';
import { BrowseCollectionsFilters } from './BrowseCollectionsFilters';
import { collectionsApi } from '../services/collectionsApi';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { Problem } from '../services/api';
import { CollectionListItem, Collection } from '../types/collection';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useAuthModal } from '../hooks/useAuthModal';
import { AuthRequiredModal } from './AuthRequiredModal';
import { LoginModal } from './LoginModal';
import { mockSystemCollections } from '../data/mockSystemCollections';
import { savePendingAction } from '../utils/authRedirectState';
import '../styles/BrowseCollectionsTab.css';

export const BrowseCollectionsTab: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const {
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess,
    handleClose
  } = useAuthModal();
  const [collections, setCollections] = useState<CollectionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedPattern, setSelectedPattern] = useState('');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [newCollectionName, setNewCollectionName] = useState('');

  useEffect(() => {
    // Always load system collections - they're public and don't require auth
    loadSystemCollections();
  }, []);

  // Listen for auth redirect completion to resume pending actions
  useEffect(() => {
    const handleAuthRedirect = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { pendingAction } = customEvent.detail;
      if (pendingAction?.type === 'add_to_my_collections') {
        const { collectionId, collectionName, collectionDescription } = pendingAction.data;

        // Find the collection in our loaded collections
        const collection = collections.find(item => item.collection.id === collectionId)?.collection;

        if (collection) {
          performAddToMyCollections(collection);
        } else {
          // If collection not found in memory, reconstruct it from saved data
          performAddToMyCollections({
            id: collectionId,
            name: collectionName,
            description: collectionDescription,
          } as Collection);
        }
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
  }, [collections]);

  const loadSystemCollections = async () => {
    try {
      setLoading(true);
      const response = await collectionsApi.getSystemCollections();
      console.log('System collections response:', response);

      // Handle both response formats (v1 and v2)
      const collectionsArray = Array.isArray(response) ? response :
                              (response.data || response.collections || []);

      // Transform the collections to the expected format
      const transformedCollections = collectionsArray.map((col: any) => ({
        collection: col,
        completedCount: 0,
        completionPercentage: 0,
        lastPracticed: undefined
      }));
      setCollections(transformedCollections);
    } catch (error) {
      console.error('Failed to load system collections, using fallback data:', error);
      // Use mock data as fallback when API fails
      const fallbackCollections = mockSystemCollections.map(col => ({
        collection: col,
        completedCount: 0,
        completionPercentage: 0,
        lastPracticed: undefined
      }));
      setCollections(fallbackCollections);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique companies from collection stats (server-computed metadata)
  const getAvailableCompanies = (): string[] => {
    const companiesSet = new Set<string>();
    collections.forEach((item) => {
      const companies = item.collection.stats?.companies || [];
      companies.forEach((company) => companiesSet.add(company));
    });
    return Array.from(companiesSet).sort();
  };

  // Extract unique patterns from collection stats (server-computed metadata)
  const getAvailablePatterns = (): string[] => {
    const patternsSet = new Set<string>();
    collections.forEach((item) => {
      const patterns = item.collection.stats?.patterns || [];
      patterns.forEach((pattern) => patternsSet.add(pattern));
    });
    return Array.from(patternsSet).sort();
  };

  // Check if a collection contains problems matching the filters (using server-computed stats)
  const collectionMatchesFilters = (collectionId: string): boolean => {
    const item = collections.find((c) => c.collection.id === collectionId);
    if (!item) return true;

    const { companies, patterns } = item.collection.stats || { companies: [], patterns: [] };

    // Check if collection has the selected company
    if (selectedCompany && !companies.includes(selectedCompany)) {
      return false;
    }

    // Check if collection has the selected pattern
    if (selectedPattern && !patterns.includes(selectedPattern)) {
      return false;
    }

    return true;
  };

  const handleClearFilters = () => {
    setSelectedCompany('');
    setSelectedPattern('');
  };

  const filteredCollections = collections.filter(item => {
    const matchesSearch = item.collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.collection.description?.toLowerCase()?.includes(searchQuery.toLowerCase()) || false) ||
                         (item.collection.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())) || false);

    const matchesFilters = collectionMatchesFilters(item.collection.id);

    return matchesSearch && matchesFilters;
  });

  // Define ratings for system collections (could be dynamic in a real app)
  const getCollectionRating = (collectionId: string): number => {
    const ratings: Record<string, number> = {
      'sys-1': 5, // Blind 75
      'sys-2': 5, // NeetCode 150
      'sys-3': 4, // Amazon
      'sys-4': 4, // Google
      'sys-5': 4, // Two Pointers
      'sys-6': 4, // DP Essentials
    };
    return ratings[collectionId] || 5;
  };

  const handleAddToMyCollections = (collection: Collection) => {
    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'add_to_my_collections',
        data: {
          collectionId: collection.id,
          collectionName: collection.name,
          collectionDescription: collection.description
        }
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => {});
      return;
    }

    // Already authenticated, proceed immediately
    performAddToMyCollections(collection);
  };

  const performAddToMyCollections = (collection: Collection) => {
    setSelectedCollection(collection);
    setNewCollectionName(collection.name);
    setShowCopyModal(true);
  };

  const handleCopyCollection = async () => {
    if (!selectedCollection || !newCollectionName.trim()) return;
    
    try {
      const newCollection = await collectionsApi.createCollection({
        name: newCollectionName.trim(),
        description: selectedCollection.description,
        type: 'custom',
        baseCollectionId: selectedCollection.id
      });
      
      // Navigate to the new collection page
      navigate(`/collections/${newCollection.id}`);
    } catch (error) {
      console.error('Failed to copy collection:', error);
      alert('Failed to create collection. Please try again.');
    }
  };

  return (
    <div className="browse-collections-tab">
      <div className="tab-header">
        <h2>Popular Collections</h2>
        <p className="tab-description">
          Explore curated collections created by our experts
        </p>
      </div>

      <div className="filters-container">
        <div className="browse-search-section">
          <input
            type="text"
            placeholder="Search collections by name, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="collections-search"
          />
        </div>
        
        <BrowseCollectionsFilters
          selectedCompany={selectedCompany}
          onCompanyChange={setSelectedCompany}
          selectedPattern={selectedPattern}
          onPatternChange={setSelectedPattern}
          availableCompanies={getAvailableCompanies()}
          availablePatterns={getAvailablePatterns()}
          onClearFilters={handleClearFilters}
        />
      </div>

      {loading ? (
        <div className="loading-state">Loading collections...</div>
      ) : filteredCollections.length === 0 ? (
        <div className="empty-state">
          <p>No collections found matching your criteria.</p>
        </div>
      ) : (
        <div className="collections-grid">
          {filteredCollections.map(item => (
            <SystemCollectionCard 
              key={item.collection.id}
              item={item}
              rating={getCollectionRating(item.collection.id)}
              onAddToMyCollections={handleAddToMyCollections}
            />
          ))}
        </div>
      )}

      {showCopyModal && selectedCollection && (
        <div className="modal-backdrop" onClick={() => setShowCopyModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Add to My Collections</h3>
            <p className="modal-description">
              This will create a copy of "{selectedCollection.name}" in your personal collections.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); handleCopyCollection(); }}>
              <div className="form-group">
                <label>Collection Name</label>
                <input
                  type="text"
                  value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  placeholder="Enter collection name"
                  autoFocus
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCopyModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="create-btn" disabled={!newCollectionName.trim()}>
                  Add to My Collections
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={handleClose}
        onOk={handleOk}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleClose}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
};
